import { useState, useEffect } from 'react';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleReject = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    localStorage.setItem('cookieConsent', JSON.stringify(onlyNecessary));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-primary shadow-2xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {!showSettings ? (
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-start space-x-3 flex-1">
              <i className="ri-cookie-line text-3xl text-primary mt-1"></i>
              <div>
                <h3 className="font-bold text-lg text-primary mb-1">
                  Usamos cookies
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Utilizamos cookies propias y de terceros para mejorar tu experiencia de navegación, 
                  analizar el tráfico del sitio y personalizar el contenido. Puedes aceptar todas las 
                  cookies, rechazarlas o configurar tus preferencias.
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleReject}
                className="whitespace-nowrap px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-colors cursor-pointer font-medium text-sm"
              >
                Rechazar
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="whitespace-nowrap px-6 py-2.5 border-2 border-primary text-primary rounded-full hover:bg-primary/5 transition-colors cursor-pointer font-medium text-sm"
              >
                Configurar
              </button>
              <button
                onClick={handleAcceptAll}
                className="whitespace-nowrap px-6 py-2.5 bg-accent text-white rounded-full hover:bg-accent/90 transition-colors cursor-pointer font-medium text-sm shadow-lg"
              >
                Aceptar todas
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-xl text-primary">Configuración de cookies</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-500 hover:text-primary cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {/* Necesarias */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <i className="ri-shield-check-line text-primary"></i>
                    <h4 className="font-bold text-primary">Cookies necesarias</h4>
                    <span className="text-xs bg-primary text-white px-2 py-0.5 rounded-full">
                      Obligatorias
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Estas cookies son esenciales para el funcionamiento del sitio web y no se pueden desactivar.
                  </p>
                </div>
                <div className="ml-4">
                  <div className="w-12 h-6 bg-primary rounded-full relative cursor-not-allowed opacity-50">
                    <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <i className="ri-line-chart-line text-primary"></i>
                    <h4 className="font-bold text-primary">Cookies analíticas</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    Nos ayudan a entender cómo los visitantes interactúan con el sitio web.
                  </p>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}
                    className={`w-12 h-6 rounded-full relative transition-colors cursor-pointer ${
                      preferences.analytics ? 'bg-primary' : 'bg-gray-300'
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        preferences.analytics ? 'right-1' : 'left-1'
                      }`}
                    ></div>
                  </button>
                </div>
              </div>

              {/* Marketing */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <i className="ri-megaphone-line text-primary"></i>
                    <h4 className="font-bold text-primary">Cookies de marketing</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    Se utilizan para mostrar anuncios relevantes y campañas de marketing efectivas.
                  </p>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                    className={`w-12 h-6 rounded-full relative transition-colors cursor-pointer ${
                      preferences.marketing ? 'bg-primary' : 'bg-gray-300'
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        preferences.marketing ? 'right-1' : 'left-1'
                      }`}
                    ></div>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleReject}
                className="whitespace-nowrap px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-colors cursor-pointer font-medium"
              >
                Rechazar todas
              </button>
              <button
                onClick={handleSavePreferences}
                className="whitespace-nowrap px-6 py-2.5 bg-accent text-white rounded-full hover:bg-accent/90 transition-colors cursor-pointer font-medium shadow-lg"
              >
                Guardar preferencias
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
